import { useEffect, useRef, useState } from 'react'
import WarningModal from './WarningModal'
import Footer from './Footer'
import AxiosInstance from 'utils/AxiosInstance'
import { useSelector } from 'react-redux'
import logoLight from '../../../pages/Login/images/kiwi-full-logo.svg'

function AIInterviewTest() {
  const [showModal, setShowModal] = useState(true)
  const [spokenTextHistory, setSpokenTextHistory] = useState([])
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [jobTitle, setJobTitle] = useState('')
  const [step, setStep] = useState('intro') // intro → wait → question → conclusion
  const [questions, setQuestions] = useState([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1)
  const [currentQuestion, setCurrentQuestion] = useState(null)
  const [timeLeft, setTimeLeft] = useState(null)
  const [conclusion, setConclusion] = useState('')

  const audioRef = useRef(null)
  const timerRef = useRef(null)
  const { theme } = useSelector((state) => state.themeConfig)

  // Format seconds as MM:SS
  const formatTime = (sec) => {
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  // Speak text with optional callback on end
  const speakWithOpenAI = async (text, onEndCallback) => {
    try {
      setIsSpeaking(true)
      const response = await fetch('https://api.openai.com/v1/audio/speech', {
        method: 'POST',
        headers: {
          Authorization: `Bearer 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`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'tts-1',
          input: text,
          voice: 'shimmer',
        }),
      })

      const blob = await response.blob()
      const audioUrl = URL.createObjectURL(blob)
      const audio = new Audio(audioUrl)

      audioRef.current = audio
      audio.play()

      audio.onended = () => {
        setIsSpeaking(false)
        URL.revokeObjectURL(audioUrl)
        if (typeof onEndCallback === 'function') onEndCallback()
      }
    } catch (error) {
      console.error('TTS error:', error)
      setIsSpeaking(false)
      if (typeof onEndCallback === 'function') onEndCallback()
    }
  }

  // Load interview data & start intro
  useEffect(() => {
    const fetchInterviewData = async () => {
      try {
        const res = await AxiosInstance.post(
          '/recruitment/ai-interview/start',
          { application_id: 52 }
        )

        setJobTitle(res.title || '')
        setQuestions(res.questions || [])
        setConclusion(res.conclusion || '')

        if (res.introduction) {
          setSpokenTextHistory([res.introduction]) // show intro in chat
          speakWithOpenAI(res.introduction, () => {
            setStep('wait')
            setTimeout(() => {
              // Start first question after 3 sec wait
              if (res.questions && res.questions.length > 0) {
                setCurrentQuestionIndex(0)
                setCurrentQuestion(res.questions[0])
                setTimeLeft(res.questions[0].time_limit || 60)
                setStep('question')
              } else {
                // no questions, go straight to conclusion
                setStep('conclusion')
                setSpokenTextHistory((prev) => [...prev, res.conclusion || ''])
                speakWithOpenAI(res.conclusion || '')
              }
            }, 3000)
          })
        }
      } catch (err) {
        console.error(err)
      }
    }

    fetchInterviewData()

    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }
      clearInterval(timerRef.current)
    }
  }, [])

  // Speak question on step change
  useEffect(() => {
    if (step === 'question' && currentQuestion) {
      speakWithOpenAI(currentQuestion.question)
    }
  }, [step, currentQuestion])

  // Timer countdown for questions
  useEffect(() => {
    if (step !== 'question' || timeLeft === null || isSpeaking) return

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current)
          handleTimeUp()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timerRef.current)
  }, [step, timeLeft, isSpeaking])

  // Handle what happens when timer hits 0
  const handleTimeUp = () => {
    // AI says time's up message then continues to next question or conclusion
    speakWithOpenAI(
      "Time is up, let's continue with the next question.",
      () => {
        const nextIndex = currentQuestionIndex + 1
        if (nextIndex < questions.length) {
          // Next question
          setCurrentQuestionIndex(nextIndex)
          setCurrentQuestion(questions[nextIndex])
          setTimeLeft(questions[nextIndex].time_limit || 60)
          setStep('question')
        } else {
          // No more questions → conclusion
          setStep('conclusion')
          setSpokenTextHistory((prev) => [...prev, conclusion])
          speakWithOpenAI(conclusion)
        }
      }
    )
  }

  return (
    <div className='ai-interview-container'>
      <div className='d-flex align-items-center w-100 position-fixed interview-header'>
        <img
          className='light-image ml-4'
          style={{ height: '50px' }}
          src={
            theme.loginLogo && theme.loginLogo.url
              ? theme.loginLogo.url
              : logoLight
          }
          alt=''
        />

        <div className='job-title-header'>
          <h1 className='text-white m-0'>AI Interview - {jobTitle}</h1>
        </div>
      </div>

      {showModal && (
        <WarningModal showModal={showModal} setShowModal={setShowModal} />
      )}

      <div className='intro-text px-4 py-2 w-50 mx-auto mb-4 scrollbar chat-body'>
        {spokenTextHistory.map((line, index) => (
          <p className='my-3' key={index}>
            {line}
          </p>
        ))}

        {step === 'question' && currentQuestion && (
          <div className='mt-4 p-3 border border-primary rounded bg-light'>
            <h5 className='mb-2'>Question:</h5>
            <p className='font-weight-bold'>{currentQuestion.question}</p>
            <p className='text-danger'>
              Time Remaining: <strong>{formatTime(timeLeft)}</strong>
            </p>
          </div>
        )}
      </div>

      <Footer isSpeaking={isSpeaking} />
    </div>
  )
}

export default AIInterviewTest
